import React from "react";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import SyncIcon from "@mui/icons-material/Sync";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { displayList } from "@auto/widgets/ListComponent";
import { rest } from "@auto/services";
import { getServiceUri } from "@auto/metadata/MetadataLoader";

import styles from "./__BackofficeUserList.module.styl";

const QALAA_USER_LIST_DEFAULT_PAGE_SIZE = 50;

let initialQalaaUserListRange = null;
let initialQalaaUserListRangeUsesLeft = 0;

const qalaaUserProfileUrl = (row) => {
  if (
    row?.id === null
    || row?.id === undefined
  ) {
    throw new Error(
      "qalaaUser.id is required to build the profile URL"
    );
  }

  return `/__backoffice-user/${row.id}`;
};

const escapeRsqlRegexValue = (value) => {
  return String(value || "")
    .trim()
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/[.*+?^{}$()|[\]\\]/g, "\\$&");
};

const buildQalaaUserSearchQuery = (search) => {
  const rawValue =
    String(search || "").trim();

  const regexValue =
    escapeRsqlRegexValue(rawValue);

  if (!regexValue) {
    return "";
  }

  const parts = [
    `username=re="(?i).*${regexValue}.*"`
  ];

  if (/^\d+$/.test(rawValue)) {
    parts.push(
      `id==${Number(rawValue)}`
    );
  }

  return parts.join(",");
};

const readQalaaUserListRangeFromUrl = () => {
  const params =
    new URLSearchParams(
      window.location.search
    );

  const offset =
    Number(params.get("offset"));

  const limit =
    Number(params.get("limit"));

  const safeLimit =
    Number.isFinite(limit) && limit > 0
      ? Math.floor(limit)
      : QALAA_USER_LIST_DEFAULT_PAGE_SIZE;

  const safeOffset =
    Number.isFinite(offset) && offset >= 0
      ? Math.floor(offset)
      : 0;

  return {
    offset: safeOffset,
    limit: safeLimit,
    page: Math.floor(
      safeOffset / safeLimit
    ),
    pageSize: safeLimit,
    hasExplicitRange:
      params.has("offset")
      || params.has("limit")
  };
};

const writeQalaaUserListRangeToUrl = ({
  offset,
  limit
}) => {
  const url =
    new URL(window.location.href);

  url.searchParams.set(
    "offset",
    String(offset)
  );

  url.searchParams.set(
    "limit",
    String(limit)
  );

  window.history.replaceState(
    {},
    "",
    url.toString()
  );
};

const getEffectiveQalaaUserListQuery = (
  query
) => {
  const search =
    String(query?.search || "").trim();

  const sort =
    Array.isArray(query?.sort)
      ? query.sort
      : [];

  const page =
    Number.isFinite(query?.page)
      ? query.page
      : 0;

  const pageSize =
    Number.isFinite(query?.pageSize)
      ? query.pageSize
      : QALAA_USER_LIST_DEFAULT_PAGE_SIZE;

  if (
    initialQalaaUserListRange
    && initialQalaaUserListRange.hasExplicitRange
    && initialQalaaUserListRangeUsesLeft > 0
    && !search
    && page === 0
    && pageSize ===
      QALAA_USER_LIST_DEFAULT_PAGE_SIZE
  ) {
    initialQalaaUserListRangeUsesLeft -= 1;

    return {
      page:
        initialQalaaUserListRange.page,

      pageSize:
        initialQalaaUserListRange.pageSize,

      offset:
        initialQalaaUserListRange.offset,

      limit:
        initialQalaaUserListRange.limit,

      search,
      sort
    };
  }

  initialQalaaUserListRangeUsesLeft = 0;

  return {
    page,
    pageSize,
    offset: page * pageSize,
    limit: pageSize,
    search,
    sort
  };
};

const selectorsForSortItem = (
  sortItem
) => {
  const column =
    sortItem?.column;

  if (
    Array.isArray(
      column?.sortSelectors
    )
  ) {
    return column.sortSelectors;
  }

  if (column?.sortSelector) {
    return [
      column.sortSelector
    ];
  }

  if (sortItem?.field) {
    return [
      sortItem.field
    ];
  }

  throw new Error(
    "Invalid sort item: missing sort selector"
  );
};

const buildSortPayload = (sort) => {
  const out = [];

  for (const sortItem of sort || []) {
    if (
      sortItem?.direction !== "asc"
      && sortItem?.direction !== "desc"
    ) {
      throw new Error(
        `Invalid sort direction: ${sortItem?.direction}`
      );
    }

    for (
      const selector
      of selectorsForSortItem(
        sortItem
      )
    ) {
      if (!selector) {
        throw new Error(
          `Invalid sort selector for field: ${sortItem?.field}`
        );
      }

      out.push({
        selector,
        direction:
          sortItem.direction
      });
    }
  }

  return out;
};

const buildQalaaUserSearchPayload = (
  range
) => {
  return {
    columns: [],

    query:
      buildQalaaUserSearchQuery(
        range.search
      ),

    limit:
      range.limit,

    offset:
      range.offset,

    sort:
      buildSortPayload(
        range.sort
      )
  };
};

const buildQalaaUserCountPayload = (
  query
) => {
  return {
    columns: [],
    query,
    limit: null,
    offset: null,
    sort: []
  };
};

const renderUsername = (row) => {
  if (!row?.username) {
    return null;
  }

  return (
    <a
      className={styles.username}
      href={qalaaUserProfileUrl(row)}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      {row.username}
    </a>
  );
};

const renderStatus = (row) => {
  const deleted =
    row?.deleted === true;

  return (
    <span
      className={
        deleted
          ? `${styles.status} ${styles.statusDeleted}`
          : `${styles.status} ${styles.statusActive}`
      }
    >
      {deleted
        ? "Deleted"
        : "Active"}
    </span>
  );
};

const renderViewAction = (
  row,
  gui
) => {
  return (
    <Tooltip title="View profile">
      <IconButton
        size="small"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          gui.goTo(
            qalaaUserProfileUrl(row)
          );
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const QalaaUserSynchronizeControl = () => {
  const [
    synchronizing,
    setSynchronizing
  ] = React.useState(false);

  const [
    error,
    setError
  ] = React.useState("");

  const synchronize = async () => {
    if (synchronizing) {
      return;
    }

    setSynchronizing(true);
    setError("");

    try {
      await rest.request(
        getServiceUri()
          + "__backoffice-role/synchronize",
        "POST",
        null
      );

      window.location.reload();

    } catch (requestError) {
      console.error(
        "Qalaa user synchronization failed",
        requestError
      );

      setError(
        "Synchronization failed."
      );

      setSynchronizing(false);
    }
  };

  return (
    <div className={styles.synchronizeControl}>
      <Button
        size="small"
        variant="outlined"
        startIcon={
          <SyncIcon fontSize="small" />
        }
        disabled={synchronizing}
        onClick={synchronize}
      >
        {synchronizing
          ? "Synchronizing..."
          : "Synchronize"}
      </Button>

      {error ? (
        <span
          className={
            styles.synchronizeError
          }
        >
          {error}
        </span>
      ) : null}
    </div>
  );
};

const qalaaUserListProjection = {
  storageKey:
    "users.backofficeUser",

  beforeNavigationControl: () => (
    <QalaaUserSynchronizeControl />
  ),

  columns: [
    {
      title: "user.qalaaId",
      field: "id",
      sortSelector: "id"
    },

    {
      title: "user.username",
      field: "username",
      sortSelector: "username",
      render: renderUsername
    },

    {
      title: "common.status",
      field: "deleted",
      sortSelector: "deleted",
      render: renderStatus
    },

    {
      title: "Actions",
      field: "__actions",
      sorting: false,
      filtering: false
    }
  ],

  actions:
    renderViewAction,

  fetch: async (query) => {
    const range =
      getEffectiveQalaaUserListQuery(
        query
      );

    writeQalaaUserListRangeToUrl({
      offset: range.offset,
      limit: range.limit
    });

    const payload =
      buildQalaaUserSearchPayload(
        range
      );

    const [
      users,
      totalCount
    ] = await Promise.all([
      rest.request(
        getServiceUri()
          + "backoffice-user/search",
        "POST",
        payload
      ),

      rest.request(
        getServiceUri()
          + "backoffice-user/count",
        "POST",
        buildQalaaUserCountPayload(
          payload.query
        )
      )
    ]);

    return {
      data:
        users || [],

      totalCount:
        Number.isFinite(
          Number(totalCount)
        )
          ? Number(totalCount)
          : 0,

      page:
        range.page
    };
  }
};

export const displayBackofficeUserList = (
  gui
) => {
  initialQalaaUserListRange =
    readQalaaUserListRangeFromUrl();

  initialQalaaUserListRangeUsesLeft =
    initialQalaaUserListRange
      .hasExplicitRange
      ? 3
      : 0;

  return displayList(
    gui,
    qalaaUserListProjection
  );
};

export default displayBackofficeUserList;